import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { claimPurchase } from "@/lib/analytics";
import Seo from "@/components/Seo";

const STEPS = ["Reading the situation.", "Building your next move.", "Checking the plan.", "Preparing your Quick Play."];

function Section({ s, v }) {
  if (v == null) return null;
  const k = s.kind;
  return (
    <section className="mt-10" data-testid={`result-section-${s.key}`}>
      <div className="flex items-baseline gap-4">
        <span className="font-[IBM_Plex_Mono] text-[color:var(--vt-amber)] text-2xl">{s.num}</span>
        <h2 className="font-head text-[color:var(--vt-body-on-cream)] text-3xl lg:text-4xl uppercase">{s.title}</h2>
      </div>
      <hr className="border-0 border-t-2 border-[color:var(--vt-amber)] w-16 mt-2 mb-5" />
      {k === "list" && <ul className="space-y-2 list-disc pl-5">{v.map((i, n) => <li key={n} className="text-[color:var(--vt-body-on-cream)]">{i}</li>)}</ul>}
      {k === "phrases" && v.map((i, n) => (
        <blockquote key={n} className="mb-4 pl-4 border-l-4 border-[color:var(--vt-amber)]">
          <p className="font-head text-xl text-[color:var(--vt-body-on-cream)] uppercase leading-tight">{i.phrase}</p>
          <p className="text-sm text-[color:var(--vt-graphite)] mt-1">{i.why}</p>
        </blockquote>
      ))}
      {k === "reasons" && <div className="space-y-3">{v.map((i, n) => (
        <div key={n} className="flex gap-3"><span className="text-[color:var(--vt-amber)] font-bold">×</span><div>
          <p className="font-semibold text-[color:var(--vt-body-on-cream)]">{i.text}</p>
          <p className="text-sm text-[color:var(--vt-graphite)]">{i.reason}</p></div></div>
      ))}</div>}
      {k === "guide" && <div className="text-[color:var(--vt-body-on-cream)] space-y-2">
        <p><strong>Start with:</strong> {v.opener}</p>
        <p><strong>Ask:</strong></p><ul className="list-disc pl-5">{(v.questions || []).map((q, n) => <li key={n}>{q}</li>)}</ul>
        <p><strong>Then listen:</strong> {v.listening}</p></div>}
      {k === "closing" && <div className="text-[color:var(--vt-body-on-cream)]"><p>{v.lesson}</p>{v.reminder && <p className="mt-2 italic">{v.reminder}</p>}</div>}
      {k === "timeline" && (() => { let t = 0; return (
        <table className="w-full text-sm border-collapse" data-testid="result-timeline">
          <thead><tr className="text-left font-[IBM_Plex_Mono] text-xs uppercase text-[color:var(--vt-label-on-cream)] border-b-2 border-[color:var(--vt-dark)]">
            <th className="py-2 pr-3">Block</th><th className="py-2 pr-3">Time</th><th className="py-2 pr-3">Min</th><th className="py-2">Focus</th></tr></thead>
          <tbody>{(v || []).map((b, n) => { const start = t; t += Number(b.minutes) || 0; return (
            <tr key={n} className="border-b border-[color:var(--vt-rule-dark)]/20 text-[color:var(--vt-body-on-cream)]">
              <td className="py-2 pr-3 font-semibold">{b.block}</td><td className="py-2 pr-3">{start}–{t} min</td>
              <td className="py-2 pr-3">{b.minutes}</td><td className="py-2">{b.focus}</td></tr>); })}</tbody>
        </table>); })()}
      {k === "blocks" && <div className="space-y-4">{(v || []).map((b, n) => (
        <div key={n} className="border-2 border-[color:var(--vt-rule-dark)]/30 p-4">
          <h3 className="font-head text-xl text-[color:var(--vt-body-on-cream)] uppercase">{b.name} · {b.minutes} min</h3>
          <p className="mt-2 text-sm text-[color:var(--vt-body-on-cream)]"><strong>Setup:</strong> {b.setup}</p>
          <p className="mt-1 text-sm text-[color:var(--vt-body-on-cream)]"><strong>How it runs:</strong> {b.how_it_runs}</p>
          {b.coaching_points && <ul className="mt-1 list-disc pl-5 text-sm text-[color:var(--vt-body-on-cream)]">{b.coaching_points.map((c, i) => <li key={i}>{c}</li>)}</ul>}
          <p className="mt-1 text-sm text-[color:var(--vt-body-on-cream)]"><strong>Success looks like:</strong> {b.success}</p>
          <p className="mt-1 text-xs text-[color:var(--vt-graphite)]">Adjust: {b.adjustment}</p>
        </div>))}</div>}
      {(k === "text") && <p className="text-[color:var(--vt-body-on-cream)] leading-relaxed">{v}</p>}
    </section>
  );
}

export default function QuickPlayResult() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const poll = useRef(null);

  useEffect(() => {
    const tick = setInterval(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), 3000);
    const check = async () => {
      try {
        const { data: o } = await api.get(`/quick-plays/order/${token}`);
        if (["completed", "escalated", "needs_attention"].includes(o.status)) {
          clearInterval(poll.current);
          claimPurchase(api, new URLSearchParams(window.location.search).get("session_id"));
          const { data: r } = await api.get(`/quick-plays/result/${token}`);
          setData(r); setStatus(r.status);
        }
      } catch { /* keep polling */ }
    };
    check();
    poll.current = setInterval(check, 3500);
    return () => { clearInterval(poll.current); clearInterval(tick); };
  }, [token]);

  if (status === "loading" || status === "checkout_started" || status === "paid" || status === "generating") {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="qp-loading">
        <span className="vt-eyebrow vt-eyebrow--cream">Building your Quick Play</span>
        <h1 className="mt-3 font-head text-[color:var(--vt-body-on-cream)] text-4xl lg:text-5xl">BUILDING YOUR QUICK PLAY</h1>
        <div className="mt-8 space-y-2">
          {STEPS.map((s, i) => (
            <p key={i} className={`text-sm ${i <= stepIdx ? "text-[color:var(--vt-body-on-cream)] font-semibold" : "text-[color:var(--vt-graphite)]"}`}>
              {i <= stepIdx ? "› " : ""}{s}
            </p>
          ))}
        </div>
      </div>
    );
  }

  if (status === "needs_attention") {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="qp-needs-attention">
        <h1 className="font-head text-[color:var(--vt-body-on-cream)] text-4xl">ALMOST THERE.</h1>
        <p className="mt-4 text-[color:var(--vt-body-on-cream)] text-lg">We're finishing your Quick Play — it'll be in your inbox within 24 hours.</p>
      </div>
    );
  }

  if (status === "escalated" && data?.escalation) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20" data-testid="qp-escalation">
        <div className="border-2 border-[color:var(--vt-dark)] bg-[#FBF9F5] p-8">
          <h1 className="font-head text-[color:var(--vt-body-on-cream)] text-3xl lg:text-4xl">{data.escalation.title}</h1>
          <p className="mt-4 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed">{data.escalation.body}</p>
          <p className="mt-6 text-[color:var(--vt-label-on-cream)] text-sm">{data.escalation.footer}</p>
          <p className="mt-4 text-sm text-[color:var(--vt-graphite)]">You were not charged.</p>
        </div>
      </div>
    );
  }

  if (status === "completed" && data) {
    const pf = data.prepared_for || {};
    const who = pf.audience === "players" ? "you" : "your athlete";
    return (
      <div data-testid="qp-result">
        <Seo title={data.title} path={`/quick-plays/r/${token}`} noindex />
        {/* cover band */}
        <section className="bg-[color:var(--vt-dark)] vt-grain">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-14">
            <span className="font-[IBM_Plex_Mono] text-[color:var(--vt-amber)] tracking-[0.18em] text-xs uppercase">Voluntold Quick Play {data.number}</span>
            <h1 className="mt-3 font-head text-[color:var(--vt-white)] text-5xl lg:text-7xl leading-[0.86]">
              {data.title_stacked.map((w) => <span key={w} className="block">{w}</span>)}
            </h1>
            <p className="mt-5 text-[color:var(--vt-body-on-dark)] text-sm">Prepared for {who}{pf.position ? ` — ${pf.position}` : ""}{pf.age ? `, age ${pf.age}` : ""}</p>
          </div>
        </section>

        <section className="vt-paper">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
            <div className="flex flex-wrap gap-3 mb-6">
              <a href={`${API}/quick-plays/result/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="qp-download-pdf" className="vt-btn vt-btn-secondary-cream vt-btn-sm">Download PDF</a>
              <ResendBtn token={token} />
              <button data-testid="qp-ask-scout" onClick={async () => {
                try {
                  const { data: s } = await api.post(`/quick-plays/result/${token}/scout`);
                  window.dispatchEvent(new CustomEvent("open-scout", { detail: { sessionId: s.session_id, intro: s.intro } }));
                } catch {}
              }} className="vt-btn vt-btn-primary vt-btn-sm">Ask Scout about this</button>
            </div>
            {data.sections.map((s) => <Section key={s.key} s={s} v={data.output[s.key]} />)}
            <div className="mt-14 border-t border-[color:var(--vt-dark)]/15 pt-8 text-center">
              <p className="font-[IBM_Plex_Mono] text-[color:var(--vt-amber)] tracking-[0.18em] text-xs uppercase">Lead today. Impact tomorrow.</p>
              <Link to="/quick-plays" className="mt-3 inline-block link-line text-sm text-[color:var(--vt-body-on-cream)]">More Quick Plays →</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return <div className="max-w-2xl mx-auto px-5 py-24 text-center"><p className="text-[color:var(--vt-body-on-cream)]">Loading…</p></div>;
}

function ResendBtn({ token }) {
  const [sent, setSent] = useState(false);
  return (
    <button data-testid="qp-email-again" onClick={async () => { try { await api.post(`/quick-plays/result/${token}/resend`); } catch {} setSent(true); }}
      className="vt-btn vt-btn-secondary-cream vt-btn-sm">{sent ? "Sent to your inbox" : "Email it to me again"}</button>
  );
}
